import React from 'react';

export interface ListProps extends React.HTMLAttributes<HTMLUListElement> {
  variant?: 'default' | 'ordered' | 'unstyled';
  size?: 'sm' | 'md' | 'lg';
  divided?: boolean;
  horizontal?: boolean;
  className?: string;
}

export interface ListItemProps extends React.HTMLAttributes<HTMLLIElement> {
  active?: boolean;
  disabled?: boolean;
  selected?: boolean;
  className?: string;
}

export const List = React.forwardRef<HTMLUListElement | HTMLOListElement, ListProps>(
  (
    { 
      variant = 'default', 
      size = 'md', 
      divided = false, 
      horizontal = false,
      className = '', 
      children, 
      ...props 
    }, 
    ref
  ) => {
    const sizeClasses = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    const variantClasses = {
      default: 'list-disc pl-5',
      ordered: 'list-decimal pl-5',
      unstyled: 'list-none',
    };

    // Use TypeScript conditional types to ensure correct ref type
    if (variant === 'ordered') {
      return (
        <ol
          // Cast ref to the correct type for ol element
          ref={ref as React.Ref<HTMLOListElement>}
          className={`
            text-gray-800 dark:text-gray-200
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            ${divided ? 'divide-y divide-gray-200 dark:divide-gray-700' : ''}
            ${horizontal ? 'flex flex-row' : 'flex flex-col'}
            ${className}
          `}
          {...props}
        >
          {children}
        </ol>
      );
    }

    return (
      <ul
        // Cast ref to the correct type for ul element
        ref={ref as React.Ref<HTMLUListElement>}
        className={`
          text-gray-800 dark:text-gray-200
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          ${divided ? 'divide-y divide-gray-200 dark:divide-gray-700' : ''}
          ${horizontal ? 'flex flex-row' : 'flex flex-col'}
          ${className}
        `}
        {...props}
      >
        {children}
      </ul>
    );
  }
);

List.displayName = 'List';

export const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  (
    { 
      active = false, 
      disabled = false, 
      selected = false, 
      className = '', 
      children, 
      ...props 
    }, 
    ref
  ) => {
    return (
      <li
        ref={ref}
        className={`
          py-2
          ${active ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
          ${selected ? 'bg-gray-100 dark:bg-gray-700/50' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
        {...(disabled ? { 'aria-disabled': true } : {})}
        {...props}
      >
        {children}
      </li>
    );
  }
);

ListItem.displayName = 'ListItem';

// Create a List.Item namespace for easier imports
const ListNamespace = Object.assign(List, {
  Item: ListItem
});

export default ListNamespace;